/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */
import { createReducer } from '@reduxjs/toolkit';

import Utils from '../../../../utils/utils';
import * as StateManager from '../../state-manager';
import * as ActionTypes from '../../actions';
import { processSharedData, setCaseViewMode } from '../helper';

const uiReducer = createReducer({}, (builder) => {
  builder
    .addCase(ActionTypes.MERGE_DATA, (state: any, action: any) => {
      const {
        propertiesToReplace = [],
        propertiesToReArrange = [],
        uiRootReference = '',
        interestPath
      } = action.payload;
      const fullRefPath =
        uiRootReference && typeof action.payload.context === 'string'
          ? `${action.payload.context}.${uiRootReference}`
          : action.payload.context;
      // type check is required for launchpad blueprint import scenario where payload.context can be object type
      const refTokens = typeof fullRefPath === 'string' ? StateManager.getReferenceTokens(fullRefPath) : [];

      const mergeOptions = {
        isArrayDeepMerge: action.payload.isArrayDeepMerge,
        propertiesToReplace: [...propertiesToReplace],
        propertiesToReArrange: [...propertiesToReArrange]
      };

      if (uiRootReference && refTokens.length) {
        Utils.setIn(
          state,
          refTokens,
          StateManager.mergeDeep(
            Utils.getIn(state, refTokens),
            Utils.getIn(action.payload.data, StateManager.getReferenceTokens(uiRootReference)),
            mergeOptions
          )
        );
      } else if (interestPath) {
        // only deep merge the interestKey path keep other intact
        const currentInterestValue = Utils.getIn(state[action.payload.context], interestPath);
        const newInterestValue = Utils.getIn(action.payload.data, interestPath);
        const mergedInterestValue = StateManager.mergeDeep(currentInterestValue, newInterestValue, {
          isArrayDeepMerge: true
        });
        const contextData = Utils.getIn(action.payload.data.context_data, interestPath);
        StateManager.performReplaceAndRearrange(
          state[action.payload.context],
          [...propertiesToReplace],
          [...propertiesToReArrange]
        );
        Utils.setIn(state[action.payload.context], interestPath, mergedInterestValue);
        if (state[action.payload.context] && contextData) {
          Utils.setIn(
            state[action.payload.context].context_data,
            interestPath,
            StateManager.mergeDeep(Utils.getIn(state[action.payload.context].context_data, interestPath), contextData, {
              isArrayDeepMerge: true
            })
          );
        }
      } else {
        state[action.payload.context] = StateManager.mergeDeep(
          state[action.payload.context],
          action.payload.data,
          mergeOptions
        );
      }

      setCaseViewMode(state, action.payload, true);
      processSharedData(state, action.payload);
    })
    .addCase(ActionTypes.SET_PROPERTY, (state: any, action: any) => {
      const { context, reference, value, isArrayDeepMerge } = action.payload;
      StateManager.updateValue(state[context], reference, value, { isArrayDeepMerge });
    })
    .addCase(ActionTypes.REPLACE_PROPERTY, (state: any, action: any) => {
      const { context, reference, value } = action.payload;
      StateManager.replaceValue(state[context], reference, value);
    })
    .addCase(ActionTypes.REMOVE_ITEM, (state: any, action: any) => {
      StateManager.removeItem(state[action.payload.context], action.payload.reference);
    })
    .addCase(ActionTypes.SET_LOADING, (state: any, action: any) => {
      Utils.setIn(state[action.payload.context], 'context_data.isLoading', true);
    })
    .addCase(ActionTypes.RESET_LOADING, (state: any, action: any) => {
      Utils.setIn(state[action.payload.context], 'context_data.isLoading', false);
    })
    .addCase(ActionTypes.ADD_SHARED_DATA, (state, action: any) => {
      const { data: sharedData } = action.payload;
      processSharedData(state, { data: { shared: sharedData } });
    })
    .addCase(ActionTypes.REMOVE_SHARED_DATA, (state: any, action: any) => {
      const { dataPagesToBeRemoved = [] } = action.payload;
      dataPagesToBeRemoved.forEach((item: any) => {
        const { dataPageName, dataPageNameWithHash } = item;
        Utils.deleteIn(state.shared, [dataPageName, dataPageNameWithHash]);

        const dataPage = Utils.getIn(state.shared, [dataPageName]);
        if (dataPage && Object.keys(dataPage).length === 0) {
          Utils.deleteIn(state.shared, [dataPageName]);
        }
      });
    })
    .addCase(ActionTypes.ALTER_BLOCKING_ACTION_COUNT, (state: any, action: any) => {
      const blockingActionCounter = Utils.getIn(
        state[action.payload.context].context_data,
        'blocking_action_counter',
        0
      );
      if (action.payload.isInitiateBlockingAction) {
        Utils.setIn(state[action.payload.context].context_data, 'blocking_action_counter', blockingActionCounter + 1);
      } else {
        Utils.setIn(state[action.payload.context].context_data, 'blocking_action_counter', blockingActionCounter - 1);
      }
    });
});

export default uiReducer;
